import { useEffect, useRef, useState } from 'react';
import { Stack, Row, Text, Button, MobileSheet } from '../ui';

const API = import.meta.env.VITE_API_URL || 'https://api.ardacious.app';

interface Props {
  friend: any;
  onClose: () => void;
  onSent?: () => void;
}

export function VoiceNoteRecorder({ friend, onClose, onSent }: Props) {
  const [phase, setPhase] = useState<'idle' | 'recording' | 'preview' | 'sending' | 'sent'>('idle');
  const [durationMs, setDurationMs] = useState(0);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const startTimeRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  async function startRecording() {
    setErr(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      recorderRef.current = recorder;
      chunksRef.current = [];
      startTimeRef.current = Date.now();
      setElapsed(0);

      recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const dur = Date.now() - startTimeRef.current;
        setDurationMs(dur);
        const b = new Blob(chunksRef.current, { type: 'audio/webm' });
        setBlob(b);
        setPreviewUrl(URL.createObjectURL(b));
        stream.getTracks().forEach(t => t.stop());
        setPhase('preview');
      };

      recorder.start(100);
      setPhase('recording');
      timerRef.current = setInterval(() => setElapsed(Date.now() - startTimeRef.current), 100);
    } catch (e: any) {
      setErr('Microphone access denied: ' + e.message);
    }
  }

  function stopRecording() {
    if (timerRef.current) clearInterval(timerRef.current);
    recorderRef.current?.stop();
  }

  async function send() {
    if (!blob) return;
    setPhase('sending');
    try {
      const res = await fetch(`${API}/messages/voice?to=${friend.id}&duration=${durationMs}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'audio/webm', 'content-length': String(blob.size) },
        body: blob,
      });
      if (!res.ok) throw new Error(await res.text());
      setPhase('sent');
      if (onSent) onSent();
    } catch (e: any) {
      setErr(e.message);
      setPhase('preview');
    }
  }

  function fmt(ms: number) {
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
  }

  return (
    <MobileSheet open={true} onClose={onClose} title={`Voice note for ${friend.name}`}>
      <div>

        {phase === 'idle' && (
          <Stack gap={3} >
            <Text variant="label" muted>tap to start recording</Text>
            <button
              onClick={startRecording}
              className="h-16 w-16 rounded-full bg-negative flex items-center justify-center hover:bg-negative/80 transition-colors"
            >
              <span className="h-4 w-4 rounded-full bg-white" />
            </button>
            {err && <Text variant="label" className="text-negative text-center">{err}</Text>}
          </Stack>
        )}

        {phase === 'recording' && (
          <Stack gap={3} >
            <Row gap={2} >
              <span className="h-2 w-2 rounded-full bg-negative animate-pulse" />
              <Text variant="small" className="tnum">{fmt(elapsed)}</Text>
            </Row>
            <button
              onClick={stopRecording}
              className="h-16 w-16 rounded-full bg-ink flex items-center justify-center hover:bg-ink/80 transition-colors"
            >
              <span className="h-5 w-5 rounded-sm bg-white" />
            </button>
            <Text variant="label" muted>tap to stop</Text>
          </Stack>
        )}

        {(phase === 'preview' || phase === 'sending') && previewUrl && (
          <Stack gap={3}>
            <Stack gap={1}>
              <Text variant="micro">preview — {fmt(durationMs)}</Text>
              <audio src={previewUrl} controls className="w-full" />
            </Stack>
            {err && <Text variant="label" className="text-negative">{err}</Text>}
            <Row gap={1} justify="end">
              <Button variant="ghost" size="sm" onClick={() => { setPhase('idle'); setBlob(null); setPreviewUrl(null); }}>
                re-record
              </Button>
              <Button variant="primary" size="sm" disabled={phase === 'sending'} onClick={send}>
                {phase === 'sending' ? 'sending…' : 'Send voice note'}
              </Button>
            </Row>
          </Stack>
        )}

        {phase === 'sent' && (
          <Stack gap={2} >
            <Text variant="small" className="text-positive">✓ Voice note sent!</Text>
            <Button variant="ghost" size="sm" onClick={onClose}>close</Button>
          </Stack>
        )}
      </div>
    </MobileSheet>
  );
}
